import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Upload } from "lucide-react";
import { api, imgUrl, formatApiError } from "../../api";

const emptyForm = { name: "", slug: "", description: "", image: null, order: 0, is_active: true, parent_id: "" };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/categories/tree", { params: { include_inactive: true } });
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = (parent = null) => {
    setEditing(null);
    setForm({ ...emptyForm, parent_id: parent?.id || "", order: parent ? (parent.children?.length || 0) + 1 : categories.length + 1 });
    setShowForm(true);
  };
  const openEdit = (category) => {
    setEditing(category);
    setForm({ name: category.name, slug: category.slug || "", description: category.description || "", image: category.image || null, order: category.order || 0,
      is_active: category.is_active, parent_id: category.parent_id || "" });
    setShowForm(true);
  };
  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/api/admin/upload", body);
      setForm((current) => ({ ...current, image: data.url }));
      toast.success("Category image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); event.target.value = ""; }
  };
  const save = async (event) => {
    event.preventDefault();
    const payload = { ...form, parent_id: form.parent_id || null };
    try {
      if (editing) await api.put(`/api/admin/categories/${editing.id}`, payload);
      else await api.post("/api/admin/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    try {
      await api.delete(`/api/admin/categories/${category.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const row = (category, child = false) => (
    <div className={`admin-table-row admin-categories-row ${child ? "is-child" : ""}`} key={category.id} data-testid={`admin-category-row-${category.id}`}>
      <span>{category.order}</span>
      <strong>{child ? `- ${category.name}` : category.name}</strong>
      <span>{category.slug}</span>
      <span className={`order-status status-${category.is_active ? "confirmed" : "cancelled"}`}>{category.is_active ? "active" : "hidden"}</span>
      <div className="row-actions">
        {!child && <button onClick={() => openNew(category)}><Plus size={14} /> Child</button>}
        <button onClick={() => openEdit(category)}><Edit3 size={14} /> Edit</button>
        <button onClick={() => remove(category)}><Trash2 size={14} /> Delete</button>
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div><span className="eyebrow">Taxonomy</span><h1>Categories.</h1></div>
        <button className="button dark-button" onClick={() => openNew()} data-testid="admin-new-category-button"><Plus size={16} /> New category</button>
      </div>
      <div className="admin-table" data-testid="admin-categories-table">
        <div className="admin-table-head admin-categories-head"><span>Order</span><span>Name</span><span>Slug</span><span>Status</span><span></span></div>
        {categories.map((parent) => <div className="admin-category-group" key={parent.id}>{row(parent)}{(parent.children || []).map((child) => row(child, true))}</div>)}
      </div>
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-panel" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-head"><h2>{editing ? "Edit category" : "New category"}</h2><button className="icon-btn" onClick={() => setShowForm(false)} aria-label="Close"><X size={18} /></button></div>
            <form onSubmit={save} className="modal-body">
              <label>Parent category<select value={form.parent_id} disabled={Boolean(editing?.children?.length)} onChange={(event) => setForm({ ...form, parent_id: event.target.value })}>
                <option value="">Top-level category</option>{categories.filter((item) => item.id !== editing?.id).map((parent) => <option key={parent.id} value={parent.id}>{parent.name}</option>)}
              </select></label>
              <label>Name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="category-name-input" /></label>
              <label>Slug (optional)<input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} /></label>
              <label>Description<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} rows={3} /></label>
              <div className="form-block">
                <span className="eyebrow">Category image</span>
                {form.image && <div className="image-preview"><img src={imgUrl(form.image)} alt="Category preview" /></div>}
                <div className="image-field-actions">
                  <label className="button outline-button"><input type="file" accept="image/*" hidden onChange={uploadImage} /><Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}</label>
                  {form.image && <button type="button" className="button outline-button" onClick={() => setForm({ ...form, image: null })}>Remove</button>}
                </div>
              </div>
              <label>Sort order<input type="number" value={form.order} onChange={(event) => setForm({ ...form, order: Number(event.target.value) })} /></label>
              <label className="toggle"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} />Visible on site</label>
              <div className="modal-foot"><button type="button" className="button outline-button" onClick={() => setShowForm(false)}>Cancel</button><button type="submit" className="button dark-button" data-testid="category-save-button">Save</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
